/**
 * The desktop app's release channel, chosen at runtime.
 *
 * One installed Denker runs Production or Preview; the local development
 * build is always `local`. Rust injects the channel into every app webview
 * before any code runs (`release_channel.rs`), so module-level constants can
 * read it synchronously. The web app has no channel: it is built per server
 * and talks to its own origin.
 */

export type ReleaseChannel = "production" | "preview" | "local";
export type SwitchableChannel = Exclude<ReleaseChannel, "local">;

export interface DesktopChannel {
  channel: ReleaseChannel;
  apiOrigin: string;
  debug: boolean;
  /** Set from the first launch after a switch until the app confirms it:
   *  the restore's id and this launch's. */
  restore: { id: string; launch: string } | null;
}

declare global {
  interface Window {
    __DENKER_CHANNEL__?: DesktopChannel;
  }
}

const CHANNEL_ORIGINS: Readonly<Record<SwitchableChannel, string>> = {
  production: "https://space.denker.ai",
  preview: "https://preview.denker.ai",
};

export function desktopChannel(): DesktopChannel | null {
  if (typeof window === "undefined") return null;
  return window.__DENKER_CHANNEL__ ?? null;
}

/** The desktop channel, or null in the web app. */
export function releaseChannel(): ReleaseChannel | null {
  return desktopChannel()?.channel ?? null;
}

/**
 * The server the desktop app talks to; "" in the web app (same origin).
 * The local build keeps its build-time server, so a developer can point it
 * at another local backend.
 */
export function desktopApiOrigin(): string {
  const desktop = desktopChannel();
  if (!desktop) return "";
  if (desktop.channel === "local") {
    return (
      (import.meta.env.VITE_DESKTOP_API_URL as string | undefined) ||
      desktop.apiOrigin
    );
  }
  return desktop.apiOrigin;
}

/** Debug tooling: on in Preview and local desktop builds and in non-release
 *  web builds, off for Production users. */
export function debugToolsEnabled(): boolean {
  const desktop = desktopChannel();
  if (desktop) return desktop.debug;
  const mode = import.meta.env.MODE;
  return mode !== "prod" && mode !== "production";
}

/** The channel a switch from `channel` leads to; local never switches. */
export function otherChannel(
  channel: ReleaseChannel | null,
): SwitchableChannel | null {
  if (channel === "production") return "preview";
  if (channel === "preview") return "production";
  return null;
}

const LOOPBACK_HOSTS = new Set(["localhost", "127.0.0.1", "[::1]"]);

function normalizedOrigin(raw: string): string | null {
  try {
    const url = new URL(raw);
    return url.protocol === "http:" || url.protocol === "https:"
      ? url.origin
      : null;
  } catch {
    return null;
  }
}

/**
 * Where an incoming `denker-app://` link belongs, from the `origin` of the
 * server that built it: `"current"` to handle it here, the other channel to
 * ask before switching, or null to ignore it. Only the Production server
 * sends links without an origin, so Preview asks to switch for them.
 */
export function linkChannel(url: string): "current" | SwitchableChannel | null {
  let origin: string | null;
  try {
    origin = new URL(url).searchParams.get("origin");
  } catch {
    return null;
  }
  const channel = releaseChannel();
  if (origin === null) return channel === "preview" ? "production" : "current";
  const normalized = normalizedOrigin(origin);
  if (!normalized) return null;
  if (normalized === normalizedOrigin(desktopApiOrigin())) return "current";
  // A local backend names itself by its web app or API, on any loopback
  // address and port, over plain http (`release_channel_kind.rs` agrees).
  if (channel === "local") {
    const { hostname, protocol } = new URL(normalized);
    return protocol === "http:" && LOOPBACK_HOSTS.has(hostname)
      ? "current"
      : null;
  }
  const target = otherChannel(channel);
  return target && CHANNEL_ORIGINS[target] === normalized ? target : null;
}
